import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { X, Plus, Trash2, ImagePlus, Link2 } from 'lucide-react';
import { Project, User, UserRole } from '../types';
import ProjectTemplateSelector from './ProjectTemplateSelector';
import { ProjectTemplate } from '../types/templates';
import { generateId } from '../utils/idGenerator';

interface CreateProjectModalProps {
  onClose: () => void;
  onCreate: (project: Project) => void;
  users: User[];
  templates?: ProjectTemplate[];
  organizationId: string;
}

export default function CreateProjectModal({ onClose, onCreate, users, organizationId, templates }: CreateProjectModalProps) {
  const [useTemplate, setUseTemplate] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    proposal: '',
  });
  const [costs, setCosts] = useState<{ description: string; amount: string; category: string }[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ userId: string; role: UserRole }[]>([]);
  const descriptionImageInputRef = useRef<HTMLInputElement>(null);

  // Define logical execution order for construction costs
  const executionOrder = [
    'Land', 'Design', 'Site Preparation', 'Foundation', 'Framing', 'Exterior', 
    'Rough-ins', 'Interior', 'Finishing', 'Marketing', 'Miscellaneous'
  ];

  const sortedCosts = useMemo(() => {
    return [...costs].sort((a, b) => {
      const aIndex = executionOrder.indexOf(a.category);
      const bIndex = executionOrder.indexOf(b.category);
      
      // If both categories are in the execution order, sort by that order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one category is in the execution order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither category is in the execution order, maintain original order
      return 0;
    });
  }, [costs]);

  const descriptionImageUrls = useMemo(() => {
    const matches = Array.from(formData.description.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g));
    return matches.map((match) => match[1]).filter(Boolean);
  }, [formData.description]);


  const handleTemplateProjectCreate = (project: Partial<Project>, template: ProjectTemplate) => {
    const newProject: Project = {
      ...project,
      id: project.id || generateId('project'),
      organizationId: organizationId,
      status: project.status || 'Draft',
      proposal: project.proposal || '',
      teamMembers: project.teamMembers || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      externalLinks: project.externalLinks || [],
      costs: project.costs || [],
    } as Project;

    onCreate(newProject);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const project: Project = {
      id: generateId('project'),
      name: formData.name,
      description: formData.description,
      status: 'Draft',
      proposal: formData.proposal,
      costs: costs.map((cost, idx) => ({
        id: generateId('cost', idx),
        description: cost.description,
        amount: parseFloat(cost.amount) || 0,
        category: cost.category,
      })),
      teamMembers,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      externalLinks: [],
    };

    onCreate(project);
  };

  const addCost = () => {
    setCosts([...costs, { description: '', amount: '', category: '' }]);
  };

  const removeCost = (index: number) => {
    setCosts(costs.filter((_, i) => i !== index));
  };

  const updateCost = (index: number, field: string, value: string) => {
    const newCosts = [...costs];
    newCosts[index] = { ...newCosts[index], [field]: value };
    setCosts(newCosts);
  };

  const addTeamMember = () => {
    if (users.length > 0) {
      setTeamMembers([...teamMembers, { userId: users[0].id, role: 'developer' }]);
    }
  };

  const removeTeamMember = (index: number) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const updateTeamMember = (index: number, field: 'userId' | 'role', value: string) => {
    const newMembers = [...teamMembers];
    newMembers[index] = { ...newMembers[index], [field]: value };
    setTeamMembers(newMembers);
  };

  const appendDescriptionSnippet = (snippet: string) => {
    setFormData((prev) => ({
      ...prev,
      description: prev.description ? `${prev.description}\n${snippet}` : snippet,
    }));
  };

  const handleInsertDescriptionLink = () => {
    const rawUrl = window.prompt('Enter link URL');
    const url = String(rawUrl || '').trim();
    if (!url) return;

    const rawLabel = window.prompt('Enter link text (optional)');
    const label = String(rawLabel || '').trim() || 'Link';
    appendDescriptionSnippet(`[${label}](${url})`);
  };

  const handleDescriptionImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = String(event.target?.result || '').trim();
        if (!imageUrl) return;

        const safeName = file.name?.trim() || `Project image ${index + 1}`;
        appendDescriptionSnippet(`![${safeName}](${imageUrl})`);
      };
      reader.readAsDataURL(file);
    });

    if (e.target) e.target.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl">Create New Project</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/40 rounded-lg"
            title="Close modal"
            aria-label="Close create project modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Toggle between template and manual */}
          <div className="mb-6 flex gap-2 p-1 bg-muted/40 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setUseTemplate(true)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                useTemplate
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-muted-foreground'
              }`}
            >
              Use Template
            </button>
            <button
              type="button"
              onClick={() => setUseTemplate(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !useTemplate
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-muted-foreground'
              }`}
            >
              Manual Setup
            </button>
          </div>

          {useTemplate ? (
            <ProjectTemplateSelector
              onProjectCreate={handleTemplateProjectCreate}
              onCancel={onClose}
              organizationId={organizationId}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div>
                <label className="block mb-2">Project Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter project name"
                />
              </div>

              <div>
                <label className="block mb-2">Description *</label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={3}
                  placeholder="Brief description of the project"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleInsertDescriptionLink}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
                  >
                    <Link2 className="h-4 w-4" />
                    Add Link
                  </button>
                  <button
                    type="button"
                    onClick={() => descriptionImageInputRef.current?.click()}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
                  >
                    <ImagePlus className="h-4 w-4" />
                    Add Picture
                  </button>
                  <input
                    ref={descriptionImageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleDescriptionImageUpload}
                    aria-label="Upload project description images"
                  />
                </div>
                {descriptionImageUrls.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {descriptionImageUrls.map((url, index) => (
                      <a
                        key={`${url}-${index}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex"
                      >
                        <img
                          src={url}
                          alt={`Description image ${index + 1}`}
                          className="h-16 w-16 rounded-lg border border-border object-cover"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2">Project Proposal</label>
                <textarea
                  value={formData.proposal}
                  onChange={(e) => setFormData({ ...formData, proposal: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={6}
                  placeholder="Detailed project proposal for stakeholder review"
                />
              </div>

              {/* Cost Assignment */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block">Cost Breakdown</label>
                  <button
                    type="button"
                    onClick={addCost}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Add Cost
                  </button>
                </div>
                {costs.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No costs added yet</p>
                ) : (
                  <div className="space-y-2">
                    {sortedCosts.map((cost, displayIndex) => {
                      // Find the original index in the costs array
                      const originalIndex = costs.findIndex(c => 
                        c.description === cost.description && 
                        c.amount === cost.amount && 
                        c.category === cost.category
                      );
                      
                      return (
                        <div key={originalIndex} className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Description"
                            value={cost.description}
                            onChange={(e) => updateCost(originalIndex, 'description', e.target.value)}
                            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Category"
                            value={cost.category}
                            onChange={(e) => updateCost(originalIndex, 'category', e.target.value)}
                            className="w-32 px-3 py-2 border border-border rounded-lg text-sm"
                          />
                          <input
                            type="number"
                            placeholder="Amount"
                            value={cost.amount}
                            onChange={(e) => updateCost(originalIndex, 'amount', e.target.value)}
                            className="w-32 px-3 py-2 border border-border rounded-lg text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeCost(originalIndex)}
                            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"
                            title={`Remove cost item ${displayIndex + 1}`}
                            aria-label={`Remove cost item ${displayIndex + 1}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Team Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block">Team Members</label>
                  <button
                    type="button"
                    onClick={addTeamMember}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Add Member
                  </button>
                </div>
                {teamMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No team members added yet</p>
                ) : (
                  <div className="space-y-2">
                    {teamMembers.map((member, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          value={member.userId}
                          onChange={(e) => updateTeamMember(index, 'userId', e.target.value)}
                          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                          title={`Team member ${index + 1}`}
                          aria-label={`Select team member for row ${index + 1}`}
                        >
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                        </select>
                        <select
                          value={member.role}
                          onChange={(e) => updateTeamMember(index, 'role', e.target.value as UserRole)}
                          className="w-40 px-3 py-2 border border-border rounded-lg text-sm"
                          title={`Role for team member ${index + 1}`}
                          aria-label={`Select role for team member row ${index + 1}`}
                        >
                          <option value="developer">Developer</option>
                          <option value="stakeholder">Stakeholder</option>
                          <option value="investor">Investor</option>
                          <option value="hr">HR</option>
                          <option value="external_partner">External Partner</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeTeamMember(index)}
                          className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"
                          title={`Remove team member ${index + 1}`}
                          aria-label={`Remove team member ${index + 1}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted/40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary/10 text-white rounded-lg hover:bg-primary/10"
                >
                  Create Project
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}